import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles'
import type { DataNode } from 'components/WorkspaceEditor/types'
import { isManufacturerOrBODPill } from 'components/WorkspaceEditor/utils'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { memo, useCallback, useMemo } from 'react'
import {
    OptionGroup,
    PlainText,
    ReplacementOption,
    SelectionOption,
    UnitsOfMeasure,
} from '.'
import { isKeyedContent } from '../ContentEditor/contentUtils'

/** Props for rendering node item */
export interface NodeContentRendererProps {
    /** The node to render */
    node: DataNode
    /** Whether the node is editable */
    editable?: boolean
    /** Whether the node is inactive */
    inactive?: boolean
}

/**
 * Renders the node item for a tree node
 * @param {NodeContentRendererProps} NodeContentRendererProps - Props for rendering node item
 * @returns {ReactElement} Styled node item
 */
export const NodeContentRenderer = memo<NodeContentRendererProps>(
    ({ node, editable = false, inactive = false }) => {
        const { type, contentItemNodes } = node
        const {
            displayContent,
            articleTypography,
            paragraphTypography,
            inactiveText,
            addedText,
            removedText,
            readOnlyCursor,
            hiddenContainer,
        } = useTreeViewStyles()
        const {
            visibilitySettingValues: { areTrackChangesVisible },
        } = useShallowEditorActionBar((state) => ({
            visibilitySettingValues: state.visibilitySettingValues,
        }))

        const plainTextClass = useMemo(
            () =>
                type === NODE_TYPES.Article
                    ? articleTypography
                    : paragraphTypography,
            [type]
        )
        const shouldHideDeleted = (change: string) =>
            change === 'DELETED' && !areTrackChangesVisible
        const getStatusClass = (changeType: string) => {
            if (inactive) return inactiveText
            if (!areTrackChangesVisible) return ''
            if (changeType === 'ADDED') return addedText
            if (changeType === 'DELETED') return removedText
            return ''
        }
        const shouldHideEmptyManufacturer = useCallback(
            (contentItem: Partial<TextContentElement>) => {
                const isOptionGroup =
                    contentItem.type === NODE_TYPES.OptionGroup
                const isManufacturerOrBOD = isManufacturerOrBODPill(node)
                const isEmpty =
                    contentItem.text?.trim() === '' ||
                    contentItem.text?.trim() === '.'

                return isManufacturerOrBOD && !isOptionGroup && isEmpty
            },
            [node]
        )

        return (
            <div className={displayContent}>
                {contentItemNodes.map((contentItem) => {
                    if (shouldHideEmptyManufacturer(contentItem)) {
                        return null
                    }

                    switch (contentItem.type) {
                        case NODE_TYPES.OptionGroup:
                            return (
                                <OptionGroup
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    editable={editable}
                                />
                            )
                        case NODE_TYPES.SelectionOption:
                        case NODE_TYPES.Hyperlink:
                            return isKeyedContent(contentItemNodes) ? (
                                <PlainText
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    editable={editable}
                                    classes={clsx(
                                        plainTextClass,
                                        inactive && inactiveText,
                                        !editable && readOnlyCursor
                                    )}
                                />
                            ) : (
                                <SelectionOption
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    editable={editable}
                                />
                            )
                        case NODE_TYPES.UnitsOfMeasure:
                            return (
                                <UnitsOfMeasure
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    editable={editable}
                                />
                            )
                        case NODE_TYPES.ReplacementOption:
                            return (
                                <ReplacementOption
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    editable={editable}
                                />
                            )
                        default:
                            const change = contentItem.changeType?.toUpperCase()

                            return (
                                <PlainText
                                    contentItem={contentItem}
                                    inactive={inactive}
                                    editable={editable}
                                    classes={clsx(
                                        shouldHideDeleted(change ?? '') &&
                                            hiddenContainer,
                                        plainTextClass,
                                        getStatusClass(change ?? ''),
                                        !editable && readOnlyCursor
                                    )}
                                />
                            )
                    }
                })}
            </div>
        )
    }
)
